/** /pre-trip-items — cross-tenant "Trip Items" catalog. Two tabs:
 *   - Items      → ResourceDataGrid over pre_trip_items (codename pre_trip_items)
 *   - Categories → ResourceDataGrid over pre_trip_item_types (codename
 *                  pre_trip_item_types)
 *
 * Each tab owns its own grid, presets, and filters. Only the ACTIVE tab's grid
 * is mounted at a time (the inactive one is unmounted) — the two grids both read
 * and write the URL preset/cols/ordering params through useGridPresets, so
 * mounting both simultaneously would make them fight over the query string. The
 * active tab lives in the `view` URL param. No reorder for either resource. */
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { Box, Button, Chip, CircularProgress, MenuItem, Tab, Tabs, TextField } from '@mui/material';
import type { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import * as itemsApi from '@/api/preTripItems';
import type { PreTripItemListItem } from '@/api/preTripItems';
import * as typesApi from '@/api/preTripItemTypes';
import { RENDERING_TYPE_LABELS, type PreTripItemTypeRow } from '@/api/preTripItemTypes';
import { deleteBatchAction, type BatchActionDef } from '@/components/DataGrid/batchTypes';
import { batchEditAction } from '@/features/batch-edit/batchEditAction';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, singleTenantId, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { AddToToursDialog } from './AddToToursDialog';
import { PreTripItemCategoryCreateDialog } from './PreTripItemCategoryCreateDialog';
import { PreTripItemCreateDialog } from './PreTripItemCreateDialog';

const digits = (v: string) => (/^\d+$/.test(v) ? Number(v) : undefined);

const MODE_LABELS: Record<string, string> = { link: 'Link', file: 'File', text: 'Text' };

type View = 'items' | 'categories';

// ---- Items tab -------------------------------------------------------------

const ITEM_FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'item_type', toApi: digits },
];
const ITEM_PRESET_FILTERS = makeResourceFiltersAdapter(ITEM_FILTERS);
const ITEM_DEFAULT_ORDER = [
  'id',
  'tenant',
  'name',
  'item_type',
  'display_mode',
  'note',
  'url',
  'deep_link',
];

/** Columns present but hidden until toggled on via the column menu. */
const ITEM_DEFAULT_HIDDEN = ['note', 'url', 'deep_link'];

interface GridProps {
  canManage: boolean;
  onNew: () => void;
  /** The operators this grid's tenant filter names (its effective tenant: URL >
   *  preset > scope), for the page's create dialogs (D9). */
  onPageTenants: (pageTenants: TenantPickerValue[]) => void;
}

function ItemsGrid({ canManage, onNew, onPageTenants }: GridProps) {
  const presets = useGridPresets({
    grid: 'pre_trip_items',
    defaultOrder: ITEM_DEFAULT_ORDER,
    defaultHidden: ITEM_DEFAULT_HIDDEN,
    defaultOrdering: 'name',
    filters: ITEM_PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    setParams,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(ITEM_FILTERS, presets.filtersLayer);

  const tenantParam = getParam('tenant');
  // The operators the effective tenant filter names (URL > preset > scope).
  const pageTenants = pageTenantsFrom(tenantParam, useTenantScopeList());
  // The create dialogs live on the page shell; hand them this grid's tenants.
  useEffect(() => {
    onPageTenants(pageTenants);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantParam]);

  const columns = useMemo<GridColDef<PreTripItemListItem>[]>(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => row.tenant?.name ?? '',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} />,
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <RouterLink
            to={`/pre-trip-items/${p.row.id}`}
            style={{ color: '#0a1a3f', fontWeight: 500 }}
          >
            {p.row.name}
          </RouterLink>
        ),
      },
      {
        field: 'item_type',
        headerName: 'Category',
        width: 180,
        sortable: false,
        valueGetter: (_, row) => row.item_type?.name ?? '',
        renderCell: (p) =>
          p.row.item_type ? (
            <RouterLink
              to={`/pre-trip-item-categories/${p.row.item_type.id}`}
              style={{ color: '#0a1a3f' }}
              onClick={(e) => e.stopPropagation()}
            >
              {p.row.item_type.name}
            </RouterLink>
          ) : (
            '—'
          ),
      },
      {
        field: 'display_mode',
        headerName: 'Type',
        width: 110,
        sortable: false,
        renderCell: (p) => (
          <Chip
            size="small"
            variant="outlined"
            label={MODE_LABELS[p.row.display_mode] ?? p.row.display_mode}
          />
        ),
      },
      {
        field: 'note',
        headerName: 'Note',
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (_, row) => row.note ?? '',
        renderCell: (p) => p.row.note || '—',
      },
      {
        field: 'url',
        headerName: 'URL',
        flex: 1,
        minWidth: 200,
        sortable: false,
        valueGetter: (_, row) => row.url ?? '',
        renderCell: (p) => p.row.url || '—',
      },
      {
        field: 'deep_link',
        headerName: 'Deep link',
        width: 110,
        sortable: false,
        renderCell: (p) => (p.row.deep_link ? '✓' : '—'),
      },
    ],
    [],
  );

  // The single-tenant extras (the category filter, Add to tours' operator) act
  // only when the filter names exactly one operator (D5).
  const gridTenantId = singleTenantId(pageTenants.map((t) => t.id));

  // The batch "Add to tours" dialog needs each selected item's tenant to decide
  // whether the selection is cross-tenant. ResourceDataGrid owns the query, so
  // we record the last page's id→tenant map from the queryFn results and lift the
  // selection via onSelectionChange, then join the two. keepNonExistentRowsSelected
  // can carry ids across pages → a missing id contributes `null` (unknown → the
  // server stays authoritative).
  const rowTenantById = useRef<Map<number, number | null>>(new Map());
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    async (params: ResourceDataGridParams) => {
      const res = await itemsApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'name',
        search: params.search,
        ...(params.extra as Record<string, never>),
      });
      for (const row of res.results) rowTenantById.current.set(row.id, row.tenant?.id ?? null);
      return res;
    },
    [orderingFallback],
  );

  const selectedRowTenantIds = useMemo(
    () => selectedIds.map((id) => rowTenantById.current.get(id) ?? null),
    [selectedIds],
  );

  const batchActions = useMemo<BatchActionDef[]>(() => {
    if (!canManage) return [];
    return [
      {
        key: 'add_to_tours',
        label: 'Add to tours',
        icon: <PlaylistAddIcon fontSize="small" />,
        action: 'add_to_tours',
        permission: 'console_pretrip_manage',
        dialog: (ctx) => (
          <AddToToursDialog
            ctx={ctx}
            gridTenantId={gridTenantId}
            selectedRowTenantIds={selectedRowTenantIds}
          />
        ),
      },
      deleteBatchAction('console_pretrip_manage'),
      batchEditAction('pre_trip_items', 'pre-trip item'),
    ];
  }, [canManage, gridTenantId, selectedRowTenantIds]);

  const onSelectionChange = useCallback(
    (model: GridRowSelectionModel) =>
      setSelectedIds(model.map((i) => Number(i)).filter(Number.isFinite)),
    [],
  );

  const categoryParam = getParam('item_type');
  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            // One write: the category belongs to the tenant it was picked under.
            // (Two setParam calls each start from the same render's URL, so the
            // second one undid the tenant.)
            onChange={(next) =>
              setParams({ tenant: formatTenantIds(next.map((t) => t.id)), item_type: null })
            }
          />
        </Box>
      ),
    },
    {
      key: 'item_type',
      priority: 4,
      active: Boolean(categoryParam),
      node: (
        <Box sx={{ minWidth: 240 }}>
          <CategoryFilter
            tenantId={gridTenantId}
            hasValue={Boolean(categoryParam)}
            onChange={(id) => setParam('item_type', id != null ? String(id) : null)}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={onNew}>
            New item
          </Button>
        )}
      </Box>
      <ResponsiveFilterBar items={toolbarItems} />
      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<PreTripItemListItem>
          queryKey={['pre_trip_items']}
          queryFn={queryFn}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={extra}
          batchResource="pre_trip_items"
          batchActions={batchActions}
          onSelectionChange={onSelectionChange}
          defaultSort={{ field: 'name', sort: 'asc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Name or note',
            exportFileName: 'trip-items',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}

/** Category filter for the Items tab — a category picker scoped to the selected
 *  tenant (empty options, and a "pick one tenant" hint, unless exactly one tenant
 *  is chosen). Holds the picked option locally for the label; reports the id up
 *  to the URL param. When the URL param clears externally (tenant change /
 *  reset), the shown value clears. */
function CategoryFilter({
  tenantId,
  hasValue,
  onChange,
}: {
  tenantId: number | null;
  hasValue: boolean;
  onChange: (id: number | null) => void;
}) {
  const [picked, setPicked] = useState<PreTripItemTypeRow | null>(null);
  const shown = hasValue ? picked : null;
  return (
    <RemoteAutocomplete<PreTripItemTypeRow>
      label="Category"
      size="small"
      value={shown}
      getOptionLabel={(o) => o.name}
      getOptionKey={(o) => o.id}
      fetchOnEmpty={tenantId !== null}
      noOptionsText={tenantId === null ? 'Pick one tenant to filter by category' : undefined}
      queryFn={async (q) =>
        tenantId != null
          ? (
              await typesApi.list({
                tour_operator_id: tenantId,
                search: q || undefined,
                pageSize: 50,
              })
            ).results
          : []
      }
      onChange={(v) => {
        const c = (v as PreTripItemTypeRow | null) ?? null;
        setPicked(c);
        onChange(c?.id ?? null);
      }}
    />
  );
}

// ---- Categories tab --------------------------------------------------------

const TYPE_FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'rendering_type', toApi: digits },
];
const TYPE_PRESET_FILTERS = makeResourceFiltersAdapter(TYPE_FILTERS);
const TYPE_DEFAULT_ORDER = ['id', 'tenant', 'name', 'system_name', 'rendering_type', 'item_count'];

function CategoriesGrid({ canManage, onNew, onPageTenants }: GridProps) {
  const presets = useGridPresets({
    grid: 'pre_trip_item_types',
    defaultOrder: TYPE_DEFAULT_ORDER,
    defaultOrdering: 'name',
    filters: TYPE_PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(TYPE_FILTERS, presets.filtersLayer);

  const tenantParam = getParam('tenant');
  // The operators the effective tenant filter names (URL > preset > scope).
  const pageTenants = pageTenantsFrom(tenantParam, useTenantScopeList());
  // The create dialogs live on the page shell; hand them this grid's tenants.
  useEffect(() => {
    onPageTenants(pageTenants);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantParam]);

  const columns = useMemo<GridColDef<PreTripItemTypeRow>[]>(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => row.tenant?.name ?? '',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} />,
      },
      {
        field: 'name',
        headerName: 'Display name',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <RouterLink
            to={`/pre-trip-item-categories/${p.row.id}`}
            style={{ color: '#0a1a3f', fontWeight: 500 }}
          >
            {p.row.name}
          </RouterLink>
        ),
      },
      { field: 'system_name', headerName: 'System name', width: 180, sortable: false },
      {
        field: 'rendering_type',
        headerName: 'Type',
        width: 160,
        renderCell: (p) => (
          <Chip
            size="small"
            variant="outlined"
            label={p.row.rendering_type_display || `Type ${p.row.rendering_type}`}
          />
        ),
      },
      {
        field: 'item_count',
        headerName: 'Items',
        width: 90,
        sortable: false,
        valueGetter: (_, row) => row.item_count,
      },
    ],
    [],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      typesApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'name',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [orderingFallback],
  );

  const renderingParam = getParam('rendering_type') ?? '';
  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'rendering_type',
      priority: 4,
      active: renderingParam !== '',
      node: (
        <TextField
          select
          size="small"
          label="Type"
          value={renderingParam}
          sx={{ width: 200 }}
          onChange={(e) => setParam('rendering_type', e.target.value || null)}
        >
          <MenuItem value="">All types</MenuItem>
          {(Object.entries(RENDERING_TYPE_LABELS) as [string, string][]).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={onNew}>
            New category
          </Button>
        )}
      </Box>
      <ResponsiveFilterBar items={toolbarItems} />
      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<PreTripItemTypeRow>
          queryKey={['pre_trip_item_types']}
          queryFn={queryFn}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={extra}
          batchResource="pre_trip_item_types"
          batchActions={[
            deleteBatchAction('console_pretrip_manage'),
            batchEditAction('pre_trip_item_types', 'category'),
          ]}
          defaultSort={{ field: 'name', sort: 'asc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Name or system name',
            exportFileName: 'trip-item-categories',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}

// ---- Page shell ------------------------------------------------------------

export function PreTripItemsPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_pretrip_manage');
  const [sp, setSp] = useSearchParams();
  const [itemCreateOpen, setItemCreateOpen] = useState(false);
  const [categoryCreateOpen, setCategoryCreateOpen] = useState(false);
  // The tenant filter belongs to the active grid (its presets are its own), so
  // the grid reports the operators it names and both create dialogs — opened
  // from its New button or from a `?new=item|category` deep link — take them.
  // Grid effects run before this page's, so a deep link on first load already
  // sees them.
  const [pageTenants, setPageTenants] = useState<TenantPickerValue[]>([]);
  useCreateIntent(() => setItemCreateOpen(true), 'item');
  useCreateIntent(() => setCategoryCreateOpen(true), 'category');

  const view: View = sp.get('view') === 'categories' ? 'categories' : 'items';
  const setView = (v: View) => {
    // Switching tabs clears the other grid's covered params so a stale
    // preset/cols/ordering from one grid never leaks into the other.
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const p of ['preset', 'cols', 'ordering', 'tenant', 'item_type']) next.delete(p);
        if (v === 'categories') next.set('view', 'categories');
        else next.delete('view');
        return next;
      },
      { replace: true },
    );
  };

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="pre-trip-items"
        title="Trip Items"
        subtitle="Cross-tenant pre-trip item catalog and its categories."
      />

      <PreTripItemCreateDialog
        open={itemCreateOpen}
        onClose={() => setItemCreateOpen(false)}
        pageTenants={pageTenants}
      />
      <PreTripItemCategoryCreateDialog
        open={categoryCreateOpen}
        onClose={() => setCategoryCreateOpen(false)}
        pageTenants={pageTenants}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={view} onChange={(_, v) => setView(v as View)}>
          <Tab value="items" label="Items" />
          <Tab value="categories" label="Categories" />
        </Tabs>
      </Box>

      {view === 'items' ? (
        <ItemsGrid
          canManage={canManage}
          onNew={() => setItemCreateOpen(true)}
          onPageTenants={setPageTenants}
        />
      ) : (
        <CategoriesGrid
          canManage={canManage}
          onNew={() => setCategoryCreateOpen(true)}
          onPageTenants={setPageTenants}
        />
      )}
    </Box>
  );
}
